import React from "react";
import { Link } from "react-router-dom";
import { FaBalanceScale, FaCheckCircle, FaMapMarkerAlt, FaStar } from "react-icons/fa";
import { resolveHubImage } from "../../utils/b2bHubHomeMedia";
import MarketplaceAddToCartButton from "./MarketplaceAddToCartButton";
import MarketplaceProductShareButton from "./MarketplaceProductShareButton";

function resolveRibbon(item) {
  if (item.badge === "CERTIFIED" || item.verified) {
    return { label: "CERTIFIED", className: "marketplace-product-grid-card__ribbon--certified" };
  }
  if (item.stock_label && Number(item.stock_label) > 0) {
    return { label: "IN STOCK", className: "marketplace-product-grid-card__ribbon--seller" };
  }
  return null;
}

const SPEC_SLOT_COUNT = 4;

function buildGridSpecItems(items) {
  const source = (Array.isArray(items) ? items : []).slice(0, SPEC_SLOT_COUNT);
  if (!source.length) return [];

  const slots = source.map((spec) => ({ ...spec, empty: false }));
  while (slots.length < SPEC_SLOT_COUNT) {
    slots.push({ label: "", value: "", empty: true });
  }
  return slots;
}

export default function MarketplaceProductGridCard({ item, onToggleCompare, compared = false }) {
  const detailHref = item.details_href || `/marketplace/products/${item.id}`;
  const detailItems = buildGridSpecItems(item.spec_items);
  const ribbon = resolveRibbon(item);

  return (
    <article className="marketplace-product-grid-card">
      <div className="marketplace-product-grid-card__media">
        {ribbon ? (
          <span className={`marketplace-product-grid-card__ribbon ${ribbon.className}`}>{ribbon.label}</span>
        ) : null}
        <Link to={detailHref} className="marketplace-product-grid-card__media-link">
          {item.image_path ? (
            <img src={resolveHubImage(item.image_path)} alt={item.title} loading="lazy" />
          ) : (
            <div className="marketplace-product-grid-card__no-image">No image</div>
          )}
        </Link>
      </div>

      <div className="marketplace-product-grid-card__body">
        <Link to={detailHref} className="marketplace-product-grid-card__title">
          {item.title}
        </Link>

        <div className="marketplace-product-grid-card__meta">
          {item.company_name ? <span className="marketplace-product-grid-card__company">{item.company_name}</span> : null}
          {item.verified ? (
            <span className="marketplace-product-grid-card__verified">
              <FaCheckCircle aria-hidden /> Verified
            </span>
          ) : null}
          {item.location ? (
            <span className="marketplace-product-grid-card__location">
              <FaMapMarkerAlt aria-hidden /> {item.location}
            </span>
          ) : null}
        </div>

        <div className="marketplace-product-grid-card__price-row">
          {item.price_label ? <p className="marketplace-product-grid-card__price">{item.price_label}</p> : null}
          {item.rating_label ? (
            <p className="marketplace-product-grid-card__rating">
              <FaStar aria-hidden /> {item.rating_label}
            </p>
          ) : null}
        </div>

        {detailItems.length ? (
          <dl className="marketplace-product-grid-card__specs">
            {detailItems.map((spec, index) => (
              <div
                key={spec.empty ? `empty-${index}` : `${spec.label}-${spec.value}`}
                className={`marketplace-product-grid-card__spec${spec.empty ? " marketplace-product-grid-card__spec--empty" : ""}`}
                aria-hidden={spec.empty ? true : undefined}
              >
                {!spec.empty ? (
                  <>
                    <dt>{spec.label}</dt>
                    <dd>{spec.value}</dd>
                  </>
                ) : null}
              </div>
            ))}
          </dl>
        ) : null}
      </div>

      <div className="marketplace-product-grid-card__actions">
        <div className="marketplace-product-grid-card__actions-primary">
          <MarketplaceAddToCartButton
            item={item}
            className="marketplace-product-grid-card__btn marketplace-product-grid-card__btn--outline marketplace-product-grid-card__btn--cart"
          />
          <Link to={detailHref} className="marketplace-product-grid-card__btn marketplace-product-grid-card__btn--primary">
            Request for Quote
          </Link>
          <Link
            to={item.company_id ? `/verified-suppliers?company=${item.company_id}` : "/verified-suppliers"}
            className="marketplace-product-grid-card__btn marketplace-product-grid-card__btn--outline"
          >
            Contact Supplier
          </Link>
        </div>
        <div className="marketplace-product-grid-card__actions-secondary">
          <button
            type="button"
            className={`marketplace-product-grid-card__btn marketplace-product-grid-card__btn--ghost${compared ? " is-active" : ""}`}
            onClick={() => onToggleCompare?.(item.id)}
          >
            <FaBalanceScale aria-hidden /> Compare
          </button>
          <MarketplaceProductShareButton
            productId={item.id}
            title={item.title}
            sharePath={detailHref}
            imagePath={item.image_path}
            companyName={item.company_name}
            description={item.short_description || item.description}
            className="marketplace-product-grid-card__share"
            triggerClassName="marketplace-product-grid-card__btn marketplace-product-grid-card__btn--ghost marketplace-product-share__trigger"
          />
        </div>
      </div>
    </article>
  );
}
